export { formatDuration, formatCurrency, formatDate, formatPhone } from './utils'

// In production, VITE_API_URL points to the API service (e.g. https://api.example.com).
// In development, it's empty and Vite's proxy handles /v1 and /console routes.
const rawApiOrigin = (import.meta.env.VITE_API_URL ?? '').trim()

// Accept both host-style values ("https://api.example.com") and accidental
// base-path values ("https://api.example.com/v1"), then normalize to origin.
export const API_ORIGIN = rawApiOrigin
  .replace(/\/+$/, '')
  .replace(/\/v1$/i, '')
  .replace(/\/console$/i, '')

const BASE_URL = `${API_ORIGIN}/v1`

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const apiKey = localStorage.getItem('relay_api_key') ?? ''

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      ...(apiKey && { 'Authorization': `Bearer ${apiKey}` }),
      ...(body !== undefined && { 'Content-Type': 'application/json' }),
    },
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: { message?: string; code?: string } }
    throw new ApiError(
      err.error?.message ?? `HTTP ${res.status}`,
      res.status,
      err.error?.code ?? 'unknown',
    )
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

// ─── Console API (session-based auth via cookies) ──────────────────────────

async function consoleRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_ORIGIN}/console${path}`, {
    method,
    credentials: 'include',
    headers: {
      ...(body !== undefined && { 'Content-Type': 'application/json' }),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: { message?: string; code?: string } }
    throw new ApiError(
      err.error?.message ?? `HTTP ${res.status}`,
      res.status,
      err.error?.code ?? 'unknown',
    )
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

// ─── Auth types ────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  name: string
  avatarUrl?: string | null
  onboardingCompleted?: boolean
  themePreference?: 'light' | 'dark'
  hasPassword?: boolean
}

export interface OrgMembership {
  id: string
  name: string
  slug: string
  role: 'admin' | 'developer' | 'member'
  type?: 'platform' | 'workspace'
  parentOrgId?: string | null
}

export interface LoginResponse {
  user: AuthUser
  orgs: OrgMembership[]
  activeOrg: OrgMembership
}

// ─── Auth API ──────────────────────────────────────────────────────────────

export interface MagicLinkVerifyResponse {
  needsRegistration?: boolean
  email?: string
  user?: AuthUser
  orgs?: OrgMembership[]
  activeOrg?: OrgMembership
}

export interface ValidateInviteResponse {
  email: string
  role: string
  orgName: string
  hasAccount: boolean
}

export const authApi = {
  register: (data: { email: string; password: string; name: string }) =>
    consoleRequest<{ user: AuthUser; org: OrgMembership }>('POST', '/auth/register', data),
  login: (data: { email: string; password: string }) =>
    consoleRequest<LoginResponse>('POST', '/auth/login', data),
  logout: () => consoleRequest<void>('POST', '/auth/logout'),
  me: () => consoleRequest<LoginResponse>('GET', '/auth/me'),
  switchOrg: (orgId: string) =>
    consoleRequest<{ activeOrg: OrgMembership }>('POST', '/auth/switch-org', { orgId }),
  createWorkspace: (name: string, parentOrgId?: string) =>
    consoleRequest<{ org: OrgMembership }>('POST', '/auth/create-workspace', { name, parentOrgId }),
  requestMagicLink: (email: string) =>
    consoleRequest<{ ok: boolean }>('POST', '/auth/magic-link', { email }),
  verifyMagicLink: (token: string) =>
    consoleRequest<MagicLinkVerifyResponse>('POST', '/auth/magic-link/verify', { token }),
  validateInvite: (token: string) =>
    consoleRequest<ValidateInviteResponse>('GET', `/auth/validate-invite?token=${encodeURIComponent(token)}`),
  acceptInvitePublic: (data: { token: string; name?: string; password?: string; existingPassword?: string }) =>
    consoleRequest<LoginResponse>('POST', '/auth/accept-invite-public', data),
  completeOnboarding: (data?: { orgName?: string; industry?: string }) =>
    consoleRequest<{ ok: boolean }>('POST', '/auth/complete-onboarding', data),
  forgotPassword: (email: string) =>
    consoleRequest<{ ok: boolean }>('POST', '/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    consoleRequest<LoginResponse>('POST', '/auth/reset-password', { token, password }),
}

// ─── Workspace API ─────────────────────────────────────────────────────────

export interface Workspace {
  id: string
  name: string
  slug: string
  plan: string
  limits: Record<string, unknown> | null
  webhookUrl: string | null
  memberCount: number
  createdAt: string
}

export interface WorkspaceMember {
  userId: string
  email: string
  name: string
  avatarUrl?: string | null
  role: string
  membershipId: string
  joinedAt: string
}

export interface WorkspaceInvitation {
  id: string
  email: string
  role: string
  token?: string
  expiresAt: string
  createdAt: string
}

export const workspaceApi = {
  get: () => consoleRequest<Workspace>('GET', '/workspace'),
  update: (data: { name?: string; slug?: string }) => consoleRequest<Workspace>('PATCH', '/workspace', data),
  listMembers: () => consoleRequest<{ data: WorkspaceMember[] }>('GET', '/workspace/members'),
  inviteMember: (data: { email: string; role: string }) =>
    consoleRequest<{ invitation: WorkspaceInvitation }>('POST', '/workspace/invitations', data),
  removeMember: (userId: string) => consoleRequest<void>('DELETE', `/workspace/members/${userId}`),
  updateRole: (userId: string, role: string) =>
    consoleRequest<void>('PATCH', `/workspace/members/${userId}`, { role }),
  listInvitations: () => consoleRequest<{ data: WorkspaceInvitation[] }>('GET', '/workspace/invitations'),
  cancelInvitation: (id: string) => consoleRequest<void>('DELETE', `/workspace/invitations/${id}`),
  getWebhook: () => consoleRequest<{ webhookUrl: string | null }>('GET', '/workspace/webhook'),
  updateWebhook: (webhookUrl: string | null) =>
    consoleRequest<{ webhookUrl: string | null }>('PUT', '/workspace/webhook', { webhookUrl }),
  deleteWebhook: () => consoleRequest<void>('DELETE', '/workspace/webhook'),
  deleteWorkspace: () =>
    consoleRequest<{ deleted: boolean; switchTo: OrgMembership | null }>('DELETE', '/workspace'),
}

// ─── Profile API ───────────────────────────────────────────────────────────

export const profileApi = {
  get: () => consoleRequest<AuthUser>('GET', '/profile'),
  update: (data: { name?: string; avatarUrl?: string; themePreference?: 'light' | 'dark' }) => consoleRequest<AuthUser>('PATCH', '/profile', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    consoleRequest<void>('POST', '/profile/change-password', data),
  setPassword: (password: string) =>
    consoleRequest<void>('POST', '/profile/set-password', { password }),
}

// ─── Console API Keys ──────────────────────────────────────────────────────

export type ApiKeyScope = 'full_access' | 'read_write' | 'read_only'

export interface ApiKeyRecord {
  id: string
  name: string
  permissions: string[] | null
  scope: ApiKeyScope
  createdBy: string | null
  createdByName: string | null
  createdAt: string
  revokedAt?: string | null
}

export const consoleApiKeysApi = {
  list: () => consoleRequest<{ data: ApiKeyRecord[] }>('GET', '/api-keys'),
  create: (data: { name: string; scope?: ApiKeyScope }) =>
    consoleRequest<{ id: string; key: string; name: string; scope: ApiKeyScope; permissions: string[]; createdAt: string }>('POST', '/api-keys', data),
  revoke: (id: string) => consoleRequest<void>('DELETE', `/api-keys/${id}`),
}

// ─── Console Tools (AI utilities) ─────────────────────────────────────────

export const consoleToolsApi = {
  generateMockVariables: (data: { systemPrompt: string; firstMessage?: string; variables: string[] }) =>
    consoleRequest<{ variables: Record<string, string> }>('POST', '/tools/mock-variables', data),
}

// ─── Platform API (platform admin — child workspace management) ─────────────

export interface PlatformChild {
  id: string
  name: string
  slug: string
  type: string
  parentOrgId: string | null
  createdAt: string
}

export const platformApi = {
  workspaces: () => consoleRequest<{ data: PlatformChild[] }>('GET', '/platform/organizations/children'),
  createChild: (name: string) =>
    consoleRequest<{ data: PlatformChild; apiKey: string }>('POST', '/platform/organizations/children', { name }),
  renameChild: (id: string, data: { name?: string; slug?: string }) =>
    consoleRequest<{ data: PlatformChild }>('PATCH', `/platform/organizations/children/${id}`, data),
  deleteChild: (id: string) =>
    consoleRequest<void>('DELETE', `/platform/organizations/children/${id}`),
}

export class ApiError extends Error {
  status: number
  code: string
  constructor(message: string, status: number, code: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

export interface ListResponse<T> {
  data: T[]
  hasMore: boolean
  total?: number
}

// --- Agents ---
export const agentsApi = {
  list: (opts?: { limit?: number; offset?: number }) => {
    const p = new URLSearchParams()
    if (opts?.limit) p.set('limit', String(opts.limit))
    if (opts?.offset) p.set('offset', String(opts.offset))
    const qs = p.toString()
    return request<ListResponse<Agent>>('GET', `/agents${qs ? '?' + qs : ''}`)
  },
  get: (id: string) => request<Agent>('GET', `/agents/${id}`),
  create: (data: AgentCreate) => request<Agent>('POST', '/agents', data),
  update: (id: string, data: Partial<AgentCreate>) => request<Agent>('PATCH', `/agents/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/agents/${id}`),
  publish: (id: string, opts?: { versionTitle?: string; versionDescription?: string }) =>
    request<Agent>('POST', `/agents/${id}/publish`, opts ?? {}),
  unpublish: (id: string) => request<Agent>('POST', `/agents/${id}/unpublish`),
}

// --- Calls ---
export const callsApi = {
  list: (opts?: { limit?: number; offset?: number; status?: string; direction?: string }) => {
    const p = new URLSearchParams()
    if (opts?.limit) p.set('limit', String(opts.limit))
    if (opts?.offset) p.set('offset', String(opts.offset))
    if (opts?.status) p.set('status', opts.status)
    if (opts?.direction) p.set('direction', opts.direction)
    const qs = p.toString()
    return request<ListResponse<Call>>('GET', `/calls${qs ? '?' + qs : ''}`)
  },
  get: (id: string) => request<Call>('GET', `/calls/${id}`),
  createWeb: (data: { agentId: string; composedAgentId?: string; dynamicVariables?: Record<string, string> }) =>
    request<{ callId: string; accessToken: string; websocketUrl: string }>('POST', '/calls/web', data),
  createPhone: (data: { agentId: string; from: string; to: string; dynamicVariables?: Record<string, string> }) =>
    request<Call>('POST', '/calls/phone', data),
  whisper: (id: string, message: string) =>
    request<{ status: string }>('POST', `/calls/${id}/whisper`, { message }),
  terminate: (id: string) =>
    request<{ status: string }>('POST', `/calls/${id}/terminate`),
  getEvents: (id: string, opts?: { type?: string; limit?: number; offset?: number; since?: string }) => {
    const p = new URLSearchParams()
    if (opts?.type) p.set('type', opts.type)
    if (opts?.limit) p.set('limit', String(opts.limit))
    if (opts?.offset) p.set('offset', String(opts.offset))
    if (opts?.since) p.set('since', opts.since)
    const qs = p.toString()
    return request<{ callId: string; source: string; count: number; hasMore?: boolean; events: Array<{ timestamp: string; callId: string; type: string; data: unknown }> }>('GET', `/calls/${id}/events${qs ? '?' + qs : ''}`)
  },
}

// --- Call Reviews ---

export interface CallReview {
  id: string
  callId: string
  orgId: string
  reviewerId?: string
  rating?: number
  qualityTier?: 'gold_standard' | 'good' | 'acceptable' | 'poor' | 'unacceptable'
  notes?: string
  rubricScores?: Record<string, number>
  tags?: string[]
  isExemplar?: boolean
  sanitizedTranscript?: Array<{ role: string; content: string; timestamp?: number }>
  createdAt: string
  updatedAt: string
}

export interface ReviewRubric {
  id: string
  orgId: string
  agentId?: string
  name: string
  dimensions: Array<{
    name: string
    label: string
    weight: number
    description: string
  }>
  createdAt: string
  updatedAt: string
}

export const reviewsApi = {
  // Reviews for a call
  listForCall: (callId: string) =>
    request<{ data: CallReview[] }>('GET', `/calls/${callId}/reviews`),
  create: (callId: string, data: {
    rating?: number
    qualityTier?: string
    notes?: string
    rubricScores?: Record<string, number>
    tags?: string[]
    isExemplar?: boolean
  }) => request<CallReview>('POST', `/calls/${callId}/reviews`, data),
  update: (id: string, data: Partial<{
    rating: number
    qualityTier: string
    notes: string
    rubricScores: Record<string, number>
    tags: string[]
    isExemplar: boolean
  }>) => request<CallReview>('PUT', `/reviews/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/reviews/${id}`),
  // All reviews for the org
  list: (opts?: { limit?: number; offset?: number; qualityTier?: string; isExemplar?: string }) => {
    const p = new URLSearchParams()
    if (opts?.limit) p.set('limit', String(opts.limit))
    if (opts?.offset) p.set('offset', String(opts.offset))
    if (opts?.qualityTier) p.set('qualityTier', opts.qualityTier)
    if (opts?.isExemplar) p.set('isExemplar', opts.isExemplar)
    const qs = p.toString()
    return request<{ data: CallReview[] }>('GET', `/reviews${qs ? '?' + qs : ''}`)
  },
}

export const rubricsApi = {
  list: () => request<{ data: ReviewRubric[] }>('GET', '/review-rubrics'),
  create: (data: { name: string; agentId?: string; dimensions: ReviewRubric['dimensions'] }) =>
    request<ReviewRubric>('POST', '/review-rubrics', data),
  update: (id: string, data: Partial<{ name: string; dimensions: ReviewRubric['dimensions'] }>) =>
    request<ReviewRubric>('PUT', `/review-rubrics/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/review-rubrics/${id}`),
  forAgent: (agentId: string) =>
    request<{ data: ReviewRubric | null }>('GET', `/review-rubrics/for-agent/${agentId}`),
}

// --- PII Detection ---

export interface PiiDetection {
  id: number
  callId: string
  detectionPass: 'regex' | 'llm'
  originalText: string
  replacementText: string
  piiType: string
  startOffset: number
  endOffset: number
  confidence: number
  status: 'detected' | 'confirmed' | 'rejected'
  reviewedBy?: string
  transcriptIndex: number
  createdAt: string
}

export const piiApi = {
  detect: (callId: string) =>
    request<{ data: PiiDetection[]; count: number }>('POST', `/calls/${callId}/sanitize`),
  list: (callId: string) =>
    request<{ data: PiiDetection[] }>('GET', `/calls/${callId}/pii`),
  updateStatus: (callId: string, ids: number[], status: 'confirmed' | 'rejected') =>
    request<{ data: PiiDetection[] }>('PATCH', `/calls/${callId}/pii`, { ids, status }),
  confirmAll: (callId: string) =>
    request<{ sanitizedTranscript: Array<{ role: string; content: string }>; confirmedCount: number; totalDetections: number }>(
      'POST',
      `/calls/${callId}/pii/confirm-all`,
      { action: 'confirm_all' },
    ),
}

// --- Phone Numbers ---
export const phoneNumbersApi = {
  list: () => request<ListResponse<PhoneNumber>>('GET', '/phone-numbers'),
  get: (id: string) => request<PhoneNumber>('GET', `/phone-numbers/${id}`),
  create: (data: { phoneNumber: string; provider?: string; inboundAgentId?: string; outboundAgentId?: string }) =>
    request<PhoneNumber>('POST', '/phone-numbers', data),
  update: (id: string, data: Partial<{ inboundAgentId: string | null; outboundAgentId: string | null; metadata: Record<string, unknown> }>) =>
    request<PhoneNumber>('PATCH', `/phone-numbers/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/phone-numbers/${id}`),
  searchAvailable: (opts: { areaCode?: string; country?: string; contains?: string; limit?: number }) => {
    const p = new URLSearchParams()
    if (opts.areaCode) p.set('areaCode', opts.areaCode)
    if (opts.country) p.set('country', opts.country)
    if (opts.contains) p.set('contains', opts.contains)
    if (opts.limit) p.set('limit', String(opts.limit))
    return request<{ data: AvailableNumber[] }>('GET', `/phone-numbers/available?${p}`)
  },
  provision: (data: { phoneNumber: string; provider?: string; inboundAgentId?: string; outboundAgentId?: string }) =>
    request<PhoneNumber>('POST', '/phone-numbers/provision', data),
  sync: () =>
    request<{ synced: number; created: number; updated: number; skipped: number; numbers: Array<{ phoneNumber: string; id: string; created: boolean }> }>('POST', '/phone-numbers/sync', {}),
}

// --- Knowledge Bases ---
export const knowledgeBasesApi = {
  list: () => request<ListResponse<KnowledgeBase>>('GET', '/knowledge-bases'),
  get: (id: string) => request<KnowledgeBase>('GET', `/knowledge-bases/${id}`),
  create: (data: { name: string; description?: string }) =>
    request<KnowledgeBase>('POST', '/knowledge-bases', data),
  update: (id: string, data: Partial<{ name: string; description: string }>) =>
    request<KnowledgeBase>('PATCH', `/knowledge-bases/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/knowledge-bases/${id}`),
  getFiles: (id: string) => request<KnowledgeBaseFile[]>('GET', `/knowledge-bases/${id}/files`),
  deleteFile: (id: string, fileId: string) =>
    request<void>('DELETE', `/knowledge-bases/${id}/files/${fileId}`),
  uploadFile: async (id: string, file: File): Promise<KnowledgeBaseFile> => {
    const formData = new FormData()
    formData.append('file', file)
    const apiKey = localStorage.getItem('relay_api_key') ?? ''
    const res = await fetch(`${BASE_URL}/knowledge-bases/${id}/files/upload`, {
      method: 'POST',
      headers: {
        ...(apiKey && { Authorization: `Bearer ${apiKey}` }),
      },
      credentials: 'include',
      body: formData,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: { message?: string } }
      throw new ApiError(
        err.error?.message ?? `Upload failed: ${res.status}`,
        res.status,
        'upload_error',
      )
    }
    return res.json() as Promise<KnowledgeBaseFile>
  },
  query: (id: string, queryText: string, topK?: number, threshold?: number) =>
    request<{ results: ChunkResult[] }>('POST', `/knowledge-bases/${id}/query`, { query: queryText, topK: topK ?? 5, threshold: threshold ?? 0.3 }),
  renameFile: (id: string, fileId: string, filename: string) =>
    request<KnowledgeBaseFile>('PATCH', `/knowledge-bases/${id}/files/${fileId}`, { filename }),
  crawl: (id: string, data: { url: string; maxDepth?: number; limit?: number; excludePaths?: string[] }) =>
    request<{ crawlJobId: string; statusUrl: string }>('POST', `/knowledge-bases/${id}/crawl`, data),
  getCrawlStatus: (id: string, jobId: string) =>
    request<CrawlProgress>('GET', `/knowledge-bases/${id}/crawl/${jobId}`),
}

// --- Campaigns ---
export const campaignsApi = {
  list: () => request<ListResponse<Campaign>>('GET', '/campaigns'),
  get: (id: string) => request<Campaign>('GET', `/campaigns/${id}`),
  create: (data: CampaignCreate) => request<Campaign>('POST', '/campaigns', data),
  start: (id: string) => request<Campaign>('POST', `/campaigns/${id}/start`),
  pause: (id: string) => request<Campaign>('POST', `/campaigns/${id}/pause`),
  cancel: (id: string) => request<Campaign>('POST', `/campaigns/${id}/cancel`),
  addContacts: (id: string, contacts: CampaignContactUpload[]) =>
    request<{ added: number }>('POST', `/campaigns/${id}/contacts`, { contacts }),
  getResults: (id: string) => request<CampaignResults>('GET', `/campaigns/${id}/results`),
}

// --- A/B Tests ---
export const abTestsApi = {
  list: () => request<ListResponse<AbTest>>('GET', '/ab-tests'),
  get: (id: string) => request<AbTest>('GET', `/ab-tests/${id}`),
  create: (data: AbTestCreate) => request<AbTest>('POST', '/ab-tests', data),
  promote: (id: string, variant: 'a' | 'b') =>
    request<AbTest>('POST', `/ab-tests/${id}/promote`, { variant }),
  delete: (id: string) => request<void>('DELETE', `/ab-tests/${id}`),
}

// --- Usage ---
export const usageApi = {
  summary: (opts?: { startDate?: string; endDate?: string }) => {
    const p = new URLSearchParams()
    if (opts?.startDate) p.set('startDate', opts.startDate)
    if (opts?.endDate) p.set('endDate', opts.endDate)
    const qs = p.toString()
    return request<UsageSummary>('GET', `/usage${qs ? '?' + qs : ''}`)
  },
  daily: (opts?: { startDate?: string; endDate?: string }) => {
    const p = new URLSearchParams()
    if (opts?.startDate) p.set('startDate', opts.startDate)
    if (opts?.endDate) p.set('endDate', opts.endDate)
    const qs = p.toString()
    return request<{ data: UsageDaily[] }>('GET', `/usage/daily${qs ? '?' + qs : ''}`)
  },
  byAgent: () => request<Record<string, UsageSummary>>('GET', '/usage/by-agent'),
}

// --- Agent Versions ---
export const agentVersionsApi = {
  list: (agentId: string) =>
    request<{ data: AgentVersion[] }>('GET', `/agents/${agentId}/versions`),
  activate: (agentId: string, version: number) =>
    request<AgentVersion>('POST', `/agents/${agentId}/versions/${version}/activate`),
  rollback: (agentId: string, version: number) =>
    request<AgentVersion>('POST', `/agents/${agentId}/versions/${version}/rollback`),
}

// --- Settings ---
export interface ProviderStatus {
  configured: boolean
}

export interface ProviderSettings {
  providers: Record<string, ProviderStatus>
  environment: string
  port: number
}

export const settingsApi = {
  get: () => fetch(`${BASE_URL}/admin/settings`).then(r => r.json()) as Promise<ProviderSettings>,
}

export interface OrgSettings {
  piiRedactionEnabled?: boolean
  piiAutoConfirmThreshold?: number
  analysisEnabled?: boolean
  email?: {
    fromName?: string
    replyTo?: string
  }
}

export const orgSettingsApi = {
  get: () => consoleRequest<{ settings: OrgSettings }>('GET', '/settings'),
  update: (data: Partial<OrgSettings>) => consoleRequest<{ settings: OrgSettings }>('PATCH', '/settings', data),
}

// --- Types ---
export interface Agent {
  id: string
  orgId: string
  name: string
  model: { provider: string; model: string; systemPrompt: string; temperature?: number; maxTokens?: number }
  voice: { provider: string; voiceId: string; model?: string; speed?: number; stability?: number }
  transcriber?: { provider: string; model?: string; language?: string; keywords?: string[] }
  firstMessage?: string
  firstMessageDelay?: number
  settings?: Record<string, unknown>
  endpointDetection?: { mode: string; minSilenceMs?: number; semanticCompletion?: boolean; utterancePrediction?: boolean }
  tools?: unknown[]
  serverUrl?: string
  postCallAnalysis?: Array<{ name: string; type: string; description: string; choices?: string[]; examples?: string[] }>
  memory?: { enabled?: boolean; strategy?: string; retentionDays?: number; maxTokens?: number; autoSummarize?: boolean }
  pronunciationDictionary?: Array<{ word: string; alphabet: string; phoneme: string }>
  knowledgeBaseId?: string
  knowledgeBaseIds?: string[]
  metadata?: Record<string, unknown>
  debugEnabled?: boolean
  version: number
  isPublished: boolean
  publishedAt?: string
  versionTitle?: string
  versionDescription?: string
  createdAt: string
  updatedAt: string
}

export type AgentCreate = Omit<Agent, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>

export interface Call {
  id: string
  agentId: string
  type: 'phone' | 'web'
  direction: 'inbound' | 'outbound'
  status: string
  fromNumber?: string
  toNumber?: string
  startedAt?: string
  endedAt?: string
  durationMs?: number
  transcript?: TranscriptEntry[]
  sanitizedTranscript?: TranscriptEntry[]
  piiStatus?: string
  summary?: string
  sentiment?: string
  analysis?: Record<string, unknown>
  endReason?: string
  recordingUrl?: string
  stereoRecordingUrl?: string
  recordingDurationMs?: number
  recordingFormat?: string
  cost?: { sttCents: number; ttsCents: number; llmCents: number; telephonyCents: number; totalCents: number }
  latency?: {
    averageSttMs: number
    averageSpeechDurationMs?: number
    averageSttProcessingMs?: number
    averageLlmMs: number
    averageTtsMs: number
    averageResponseMs?: number
    averageTotalMs: number
  }
  metadata?: Record<string, unknown>
  dynamicVariables?: Record<string, unknown>
  _redacted?: boolean
  createdAt: string
}

export interface TranscriptEntry {
  role: 'agent' | 'user' | 'system'
  content: string
  timestamp: number
}

export interface PhoneNumber {
  id: string
  phoneNumber: string
  provider: string
  inboundAgentId?: string | null
  outboundAgentId?: string | null
  status: string
  createdAt: string
}

export interface AvailableNumber {
  phoneNumber: string
  friendlyName: string
  locality?: string
  region?: string
  capabilities: { voice: boolean; sms: boolean }
  monthlyPrice: number
}

export interface KnowledgeBase {
  id: string
  name: string
  description?: string
  embeddingModel?: string
  chunkSize?: number
  chunkOverlap?: number
  fileCount: number
  totalChunks: number
  status: string
  createdAt: string
  updatedAt?: string
}

export interface KnowledgeBaseFile {
  id: string
  knowledgeBaseId: string
  filename: string
  mimeType: string
  sizeBytes: number
  chunkCount: number | null
  status: string | null
  errorMessage: string | null
  createdAt: string
}

export interface CrawlProgress {
  status: 'starting' | 'crawling' | 'processing_pages' | 'completed' | 'failed'
  totalPages: number
  completedPages: number
  processedFiles: number
  error?: string
}

export interface ChunkResult {
  content: string
  score: number
  metadata: Record<string, unknown>
}

export interface Campaign {
  id: string
  name: string
  agentId: string
  fromNumber: string
  status: string
  schedule: Record<string, unknown>
  totalContacts: number
  completedCalls: number
  successfulCalls: number
  failedCalls: number
  createdAt: string
}

export type CampaignCreate = {
  name: string
  agentId: string
  fromNumber: string
  schedule: { maxConcurrent?: number; callsPerMinute?: number }
}

export interface CampaignContactUpload {
  phoneNumber: string
  dynamicVariables?: Record<string, string>
}

export interface CampaignResults {
  totalContacts: number
  completedCalls: number
  successfulCalls: number
  failedCalls: number
}

export interface AbTest {
  id: string
  name: string
  description?: string
  variantA: { agentId: string; label?: string }
  variantB: { agentId: string; label?: string }
  trafficSplit: { a: number; b: number }
  successMetric: string
  status: string
  results?: Record<string, unknown>
  createdAt: string
}

export type AbTestCreate = Omit<AbTest, 'id' | 'status' | 'results' | 'createdAt'>

export interface AgentVersion {
  id: string
  agentId: string
  versionNumber: number
  systemPrompt: string
  author?: string
  notes?: string
  isActive: boolean
  createdAt: string
}

export interface UsageSummary {
  totalCost: number
  totalCalls: number
  totalMinutes: number
  breakdown: { stt: number; tts: number; llm: number; telephony: number }
}

export interface UsageDaily {
  date: string
  cost: number
  calls: number
  minutes: number
}

// --- Composed Agents ---
export interface ComposedAgent {
  id: string
  orgId: string
  name: string
  routerAgentId: string
  agents: Array<{ id: string; role: string }>
  transitions: Array<{ from: string; to: string; condition: string }>
  sharedContext: boolean
  seamlessHandoff: boolean
  metadata?: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export type ComposedAgentCreate = {
  name: string
  routerAgentId: string
  agents: Array<{ id: string; role: string }>
  transitions: Array<{ from: string; to: string; condition: string }>
  sharedContext?: boolean
  seamlessHandoff?: boolean
  metadata?: Record<string, unknown>
}

// --- Voices ---

export interface Voice {
  voiceId: string
  name: string
  description: string | null
  previewUrl: string | null
  imageUrl: string | null
  category: string
  labels: {
    gender: string | null
    age: string | null
    accent: string | null
    useCase: string | null
    description: string | null
  }
}

export interface VoiceListParams {
  page_size?: number
  page?: number
  gender?: string
  age?: string
  accent?: string
  language?: string
  search?: string
  category?: string
}

export const voicesApi = {
  list: (opts?: VoiceListParams) => {
    const p = new URLSearchParams()
    if (opts?.page_size) p.set('page_size', String(opts.page_size))
    if (opts?.page) p.set('page', String(opts.page))
    if (opts?.gender) p.set('gender', opts.gender)
    if (opts?.age) p.set('age', opts.age)
    if (opts?.accent) p.set('accent', opts.accent)
    if (opts?.language) p.set('language', opts.language)
    if (opts?.search) p.set('search', opts.search)
    if (opts?.category) p.set('category', opts.category)
    const qs = p.toString()
    return request<{ data: Voice[]; hasMore: boolean }>('GET', `/voices${qs ? '?' + qs : ''}`)
  },
  preview: async (data: { voiceId: string; text: string; model?: string; stability?: number; speed?: number }): Promise<Blob> => {
    const apiKey = localStorage.getItem('relay_api_key') ?? ''
    const res = await fetch(`${BASE_URL}/voices/preview`, {
      method: 'POST',
      headers: {
        ...(apiKey && { 'Authorization': `Bearer ${apiKey}` }),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new ApiError('Preview generation failed', res.status, 'preview_error')
    return res.blob()
  },
}

// --- Composed Agents ---

export const composedAgentsApi = {
  list: (opts?: { limit?: number; offset?: number }) => {
    const p = new URLSearchParams()
    if (opts?.limit) p.set('limit', String(opts.limit))
    if (opts?.offset) p.set('offset', String(opts.offset))
    const qs = p.toString()
    return request<ListResponse<ComposedAgent>>('GET', `/agents/composed${qs ? '?' + qs : ''}`)
  },
  get: (id: string) => request<ComposedAgent>('GET', `/agents/composed/${id}`),
  create: (data: ComposedAgentCreate) => request<ComposedAgent>('POST', '/agents/compose', data),
  update: (id: string, data: Partial<ComposedAgentCreate>) => request<ComposedAgent>('PATCH', `/agents/composed/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/agents/composed/${id}`),
}

// --- Global Tools ---

export interface ToolRecord {
  id: string
  orgId: string
  source?: 'system' | 'user'
  type: 'function' | 'transfer_call' | 'agent_transfer'
  name: string
  description: string
  configuration: Record<string, unknown>
  execution?: {
    speakDuringExecution?: boolean
    speakAfterExecution?: boolean
    executionMessage?: string
    timeoutMs?: number
  } | null
  usedBy?: Array<{ agentId: string; agentName: string }>
  createdAt: string
  updatedAt: string
}

export interface ToolCreate {
  type: 'function' | 'transfer_call' | 'agent_transfer'
  name: string
  description: string
  configuration: Record<string, unknown>
  execution?: ToolRecord['execution']
}

export interface AgentToolAssignment {
  id: string
  agentId: string
  toolId: string
  priority: number
  overrides?: Record<string, unknown> | null
  createdAt: string
}

export const toolsApi = {
  list: () => request<{ data: ToolRecord[] }>('GET', '/tools'),
  get: (id: string) => request<ToolRecord>('GET', `/tools/${id}`),
  create: (data: ToolCreate) => request<ToolRecord>('POST', '/tools', data),
  update: (id: string, data: Partial<ToolCreate>) => request<ToolRecord>('PATCH', `/tools/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/tools/${id}`),
  // Agent-tool assignments
  listForAgent: (agentId: string) => request<{ data: Array<ToolRecord & { priority: number; overrides: unknown; assignmentId: string }> }>('GET', `/agents/${agentId}/tools`),
  assignToAgent: (agentId: string, data: { toolId: string; priority?: number; overrides?: unknown }) =>
    request<AgentToolAssignment>('POST', `/agents/${agentId}/tools`, data),
  removeFromAgent: (agentId: string, toolId: string) =>
    request<void>('DELETE', `/agents/${agentId}/tools/${toolId}`),
  test: (id: string, data: { arguments: Record<string, unknown>; callContext?: Record<string, string> }) =>
    request<{
      success: boolean
      status: number
      body: unknown
      extractedVariables: Record<string, unknown>
      error?: string
      durationMs: number
    }>('POST', `/tools/${id}/test`, data),
}

// --- SIP Trunks ---

export interface SipTrunk {
  id: string
  orgId: string
  name: string
  twilioTrunkSid?: string | null
  trunkDomain?: string | null
  inboundAgentId?: string | null
  routingMode: string
  authMode: string
  terminationEnabled: boolean
  terminationUri?: string | null
  status: string
  metadata?: Record<string, unknown> | null
  credentials?: Array<{ id: string; username: string; passwordMask?: string | null; status?: string }>
  ipAllowList?: Array<{ id: string; cidrBlock: string; description?: string | null }>
  signalingIps?: Record<string, string>
  ports?: { udp_tcp: number; tls: number }
  createdAt: string
  updatedAt: string
}

export interface SipTrunkCreate {
  name: string
  inboundAgentId?: string
  routingMode?: 'agent' | 'did' | 'header'
  authMode?: 'ip_acl' | 'credential' | 'both'
  ipAllowList?: Array<{ cidrBlock: string; description?: string }>
  credentials?: Array<{ username: string; password: string }>
  metadata?: Record<string, unknown>
}

export const sipTrunksApi = {
  list: () => request<{ data: SipTrunk[] }>('GET', '/sip-trunks'),
  get: (id: string) => request<SipTrunk>('GET', `/sip-trunks/${id}`),
  create: (data: SipTrunkCreate) => request<SipTrunk>('POST', '/sip-trunks', data),
  update: (id: string, data: Partial<{
    name: string
    inboundAgentId: string | null
    routingMode: string
    authMode: string
    terminationEnabled: boolean
    terminationUri: string | null
    status: string
    metadata: Record<string, unknown>
  }>) => request<SipTrunk>('PATCH', `/sip-trunks/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/sip-trunks/${id}`),
  addCredential: (trunkId: string, data: { username: string; password: string }) =>
    request<{ id: string; username: string; passwordMask: string }>('POST', `/sip-trunks/${trunkId}/credentials`, data),
  removeCredential: (trunkId: string, credId: string) =>
    request<void>('DELETE', `/sip-trunks/${trunkId}/credentials/${credId}`),
  addIpAcl: (trunkId: string, data: { cidrBlock: string; description?: string }) =>
    request<{ id: string; cidrBlock: string; description?: string }>('POST', `/sip-trunks/${trunkId}/ip-acls`, data),
  removeIpAcl: (trunkId: string, aclId: string) =>
    request<void>('DELETE', `/sip-trunks/${trunkId}/ip-acls/${aclId}`),
}

// --- Transfer Directory ---

export interface TransferList {
  id: string
  orgId: string
  name: string
  description: string | null
  createdAt: string
  updatedAt: string
}

export interface TransferDirectoryEntry {
  id: string
  orgId: string
  listId: string | null
  name: string
  description: string | null
  destination: string
  destinationType: 'phone' | 'sip'
  extension: string | null
  hours: Record<string, string> | null
  metadata: Record<string, unknown>
  enabled: boolean
  createdAt: string
  updatedAt: string
}

// --- Analytics ---

export interface LatencyPercentiles {
  p50: number
  p95: number
  p99: number
}

export interface LatencyPercentileResponse {
  period: string
  totalCalls: number
  overall: LatencyPercentiles
  stt: LatencyPercentiles
  llm: LatencyPercentiles
  tts: LatencyPercentiles
  daily: Array<{ date: string; p50: number; p95: number }>
}

export const analyticsApi = {
  latencyPercentiles: (period = '7d', agentId?: string) => {
    const params = new URLSearchParams({ period })
    if (agentId) params.set('agentId', agentId)
    return request<LatencyPercentileResponse>('GET', `/analytics/latency?${params}`)
  },
}

// --- Transfer Lists ---

export const transferListsApi = {
  list: () => request<{ data: TransferList[] }>('GET', '/transfer-lists'),
  create: (data: { name: string; description?: string }) =>
    request<TransferList>('POST', '/transfer-lists', data),
  update: (id: string, data: { name?: string; description?: string | null }) =>
    request<TransferList>('PATCH', `/transfer-lists/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/transfer-lists/${id}`),
}

// --- Heartbeat Agents ---

export interface HeartbeatTrigger {
  type: 'error_rate' | 'short_calls' | 'transfers' | 'negative_sentiment' | 'call_volume' | 'any_activity' | 'manual'
  threshold?: number
  window?: string
  cooldown?: string
}

export interface HeartbeatTask {
  id: string
  orgId: string
  name: string
  prompt: string
  contextType: string
  contextConfig: Record<string, unknown>
  trigger: HeartbeatTrigger
  targetAgentId: string | null
  provider: string
  model: string
  maxTokens: number
  enabled: boolean
  lastRunAt: string | null
  createdAt: string
  updatedAt: string
}

export interface HeartbeatRun {
  id: string
  taskId: string
  orgId: string
  startedAt: string
  completedAt: string | null
  prompt: string
  context: string | null
  result: string | null
  tokensUsed: number
  durationMs: number
  status: string
  error: string | null
  createdAt: string
}

export interface CreateHeartbeatTaskInput {
  name: string
  prompt: string
  contextType?: string
  contextConfig?: Record<string, unknown>
  trigger?: Partial<HeartbeatTrigger>
  targetAgentId?: string | null
  provider?: string
  model?: string
  maxTokens?: number
  enabled?: boolean
  notifyIntegrationIds?: string[]
}

export const heartbeatAgentsApi = {
  list: () => request<ListResponse<HeartbeatTask>>('GET', '/heartbeat-tasks'),
  get: (id: string) => request<HeartbeatTask>('GET', `/heartbeat-tasks/${id}`),
  create: (data: CreateHeartbeatTaskInput) => request<HeartbeatTask>('POST', '/heartbeat-tasks', data),
  update: (id: string, data: Partial<CreateHeartbeatTaskInput>) =>
    request<HeartbeatTask>('PATCH', `/heartbeat-tasks/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/heartbeat-tasks/${id}`),
  runs: (id: string) => request<ListResponse<HeartbeatRun>>('GET', `/heartbeat-tasks/${id}/runs`),
  trigger: (id: string) => request<{ message: string; taskId: string }>('POST', `/heartbeat-tasks/${id}/run`),
}

// --- Integrations ---

export type Integration = {
  id: string
  orgId: string
  type: 'slack'
  name: string
  enabled: boolean
  config: {
    webhookUrl: string
    hasWebhookUrl: boolean
    channel?: string
    severityFilter: ('critical' | 'warning' | 'info')[]
    notifyOnError: boolean
  }
  createdAt: string
  updatedAt: string
}

export type NotificationLogEntry = {
  id: string
  integrationId: string
  orgId: string
  heartbeatRunId: string | null
  eventType: string
  severity: string | null
  payload: unknown
  status: 'sent' | 'failed' | 'skipped'
  error: string | null
  responseStatus: number | null
  latencyMs: number | null
  createdAt: string
}

export type CreateIntegrationInput = {
  type: 'slack'
  name: string
  enabled?: boolean
  config: {
    webhookUrl: string
    channel?: string
    severityFilter: ('critical' | 'warning' | 'info')[]
    notifyOnError?: boolean
  }
}

export type UpdateIntegrationInput = {
  name?: string
  enabled?: boolean
  config?: Partial<CreateIntegrationInput['config']>
}

export const integrationsApi = {
  list: () => consoleRequest<{ data: Integration[] }>('GET', '/integrations'),
  create: (data: CreateIntegrationInput) => consoleRequest<Integration>('POST', '/integrations', data),
  update: (id: string, data: UpdateIntegrationInput) =>
    consoleRequest<Integration>('PATCH', `/integrations/${id}`, data),
  delete: (id: string) => consoleRequest<void>('DELETE', `/integrations/${id}`),
  test: (id: string) => consoleRequest<{ success: boolean; message?: string; error?: string }>('POST', `/integrations/${id}/test`),
  history: (id: string, opts?: { limit?: number; offset?: number }) => {
    const params = new URLSearchParams()
    if (opts?.limit) params.set('limit', String(opts.limit))
    if (opts?.offset) params.set('offset', String(opts.offset))
    const qs = params.toString()
    return consoleRequest<{ data: NotificationLogEntry[] }>('GET', `/integrations/${id}/history${qs ? `?${qs}` : ''}`)
  },
}

// --- Transfer Directory ---

export const transferDirectoryApi = {
  list: () => request<{ data: TransferDirectoryEntry[] }>('GET', '/transfer-directory'),
  create: (data: Omit<TransferDirectoryEntry, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>) =>
    request<TransferDirectoryEntry>('POST', '/transfer-directory', data),
  update: (id: string, data: Partial<TransferDirectoryEntry>) =>
    request<TransferDirectoryEntry>('PATCH', `/transfer-directory/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/transfer-directory/${id}`),
  import: (entries: Array<Omit<TransferDirectoryEntry, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>>) =>
    request<{ data: TransferDirectoryEntry[]; count: number }>('POST', '/transfer-directory/import', { entries }),
}

// ── Template types ────────────────────────────────────────────────────────────

export interface AgentTemplateSummary {
  id: string;
  name: string;
  agentName: string;
  industry: string;
  icon: string;
  description: string;
  tags: string[];
}

// ── Templates API ─────────────────────────────────────────────────────────────

export const templatesApi = {
  listAgents: (params?: { industry?: string; tag?: string; search?: string }) => {
    const entries = Object.entries(params ?? {}).filter(([, v]) => v != null) as [string, string][];
    const qs = entries.length ? new URLSearchParams(entries).toString() : '';
    return request<{ data: AgentTemplateSummary[] }>('GET', `/templates/agents${qs ? '?' + qs : ''}`);
  },
};
